import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { getCategoryName } from "../../config/categories";
import { getRangePath } from "../../config/ranges";
import StatusBadge from "./StatusBadge";
import ProductGalleryPlaceholder from "./ProductGalleryPlaceholder";
import { getPrimaryProductImage } from "../../lib/productMedia";

/**
 * Master range card for Products / Shop catalogue browse.
 */
export default function RangeCard({ range, index = 0, mode = "catalog" }) {
  const to = getRangePath(range);
  const categoryLabel = range.subcategory || getCategoryName(range.category);
  const cta = mode === "shop" ? "View Range" : "View Range";

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: (index % 6) * 0.06, ease: [0.19, 1, 0.22, 1] }}
      className="steel-card clip-frame group flex flex-col h-full"
      data-testid={`range-card-${range.slug}`}
    >
      <Link to={to} className="block border-b border-border overflow-hidden">
        {getPrimaryProductImage(range) ? (
          <img
            src={getPrimaryProductImage(range)}
            alt={range.name}
            className="w-full aspect-[16/10] object-cover object-center group-hover:scale-[1.02] transition-transform duration-700"
            loading="lazy"
          />
        ) : (
          <ProductGalleryPlaceholder compact label="Range Imagery" />
        )}
      </Link>

      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="font-mono-2 text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
            {categoryLabel}
          </div>
          <StatusBadge status={range.status} />
        </div>

        <Link to={to} className="mt-3 block">
          <h3 className="display display-section text-xl md:text-2xl leading-tight group-hover:text-[color:var(--gold)] transition-colors duration-500">
            {range.name}
          </h3>
        </Link>

        {range.rangeLabel && (
          <div className="mt-2 font-mono-2 text-[11px] tracking-[0.18em] uppercase text-[color:var(--gold)]">
            Available · {range.rangeLabel}
          </div>
        )}

        <p className="mt-3 text-sm text-steel-text leading-relaxed line-clamp-3 flex-1">
          {range.shortDescription || range.description}
        </p>

        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="font-mono-2 text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
            {range.quoteEnabled ? "Quote Mode" : "Overview"}
          </div>
          <Link
            to={to}
            className="inline-flex items-center gap-1.5 font-mono-2 text-[11px] tracking-[0.18em] uppercase"
            style={{ color: "var(--gold)" }}
            data-testid={`range-view-${range.slug}`}
          >
            {cta} <ArrowUpRight size={13} />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
